import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { supabase } from '@/integrations/supabase/client';
import { track } from '@/lib/analytics';
import { readQualifyData, clearQualifyData, buildMessageBlock, type QualifyData } from '@/lib/producer-qualify';

export default function ForProducersApply() {
  const [submitting, setSubmitting] = useState(false);
  const [name, setName] = useState('');
  const [region, setRegion] = useState('');
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [done, setDone] = useState(false);
  const prefilled = useRef(false);
  const qualifyRef = useRef<QualifyData | null>(null);

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session?.user?.email) setEmail(session.user.email);
    });

    if (!prefilled.current) {
      prefilled.current = true;
      const qd = readQualifyData();
      qualifyRef.current = qd;
      if (qd) {
        // Pre-fill from v2 qualify data
        if (qd.producer_name) setName(qd.producer_name);
        if (qd.selected_place) {
          const placeParts = [qd.selected_place.municipality, qd.selected_place.landskap].filter(Boolean);
          setRegion(placeParts.join(', '));
        }
        setMessage(buildMessageBlock(qd));
      }
    }
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim() || !email.trim()) return;
    setSubmitting(true);
    setErrorMsg(null);

    try {
      track('producer_interest_submit_started', {} as Record<string, never>);
    } catch {}

    try {
      // Build final message
      const BLOCK_MARKER = '---\n';
      let userText = message.trim();
      const markerIdx = userText.indexOf(BLOCK_MARKER);
      if (markerIdx !== -1) {
        const beforeBlock = userText.slice(0, markerIdx).trimEnd();
        const lastMarker = userText.lastIndexOf('\n---');
        const afterBlock = lastMarker > markerIdx ? userText.slice(lastMarker + 4).trimStart() : '';
        userText = [beforeBlock, afterBlock].filter(Boolean).join('\n\n');
      }
      const qd = qualifyRef.current;
      const qualifyBlock = qd ? buildMessageBlock(qd) : '';
      const finalMessage = [userText, qualifyBlock].filter(Boolean).join('\n\n');

      const res = await supabase.functions.invoke('producer-interest', {
        body: {
          business_name: name.trim(),
          area_text: region.trim(),
          email: email.trim(),
          message: finalMessage,
        },
      });

      if (res.error) {
        const body = res.data as { error?: string; message_sv?: string } | null;
        if (body?.error === 'rate_limited') {
          setErrorMsg(body.message_sv ?? 'Försök igen om en stund.');
        } else if (body?.error === 'validation') {
          setErrorMsg('Kontrollera att namn och e-post är ifyllda.');
        } else {
          setErrorMsg('Något gick fel. Försök igen.');
        }
        try { track('producer_interest_submit_failed', { error_kind: body?.error ?? 'unknown' }); } catch {}
        return;
      }

      try { track('producer_interest_submit_succeeded', {} as Record<string, never>); } catch {}
      clearQualifyData();
      setDone(true);
    } catch {
      setErrorMsg('Något gick fel. Försök igen.');
      try { track('producer_interest_submit_failed', { error_kind: 'network' }); } catch {}
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div className="min-h-screen bg-background flex flex-col">
        <div className="p-4">
          <Link
            to="/"
            className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground transition-colors"
          >
            <ChevronLeft className="h-4 w-4" />
            Tillbaka
          </Link>
        </div>
        <main className="flex-1 flex items-center justify-center px-6 pb-20">
          <div className="max-w-md w-full space-y-6 text-center">
            <h2 className="font-display text-xl text-foreground">Tack!</h2>
            <div className="space-y-3 text-sm text-muted-foreground leading-relaxed">
              <p>Vi går igenom alla förslag manuellt och återkommer när vi kan.</p>
              <p>Det brukar ta några dagar.</p>
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <div className="p-4">
        <Link
          to="/for-producers/qualify"
          className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground transition-colors"
        >
          <ChevronLeft className="h-4 w-4" />
          Tillbaka
        </Link>
      </div>

      <main className="flex-1 flex items-start justify-center px-6 pb-20 pt-8 md:pt-16">
        <div className="max-w-md w-full">
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-1">
              <h2 className="font-display text-xl text-foreground">
                Berätta lite om er
              </h2>
              <p className="text-sm text-muted-foreground">
                Ingen registrering. Vi går igenom manuellt.
              </p>
            </div>

            <div className="space-y-4">
              <div>
                <label htmlFor="name" className="text-sm text-muted-foreground block mb-1">
                  Namn på verksamheten
                </label>
                <Input id="name" value={name} onChange={(e) => setName(e.target.value)} required autoFocus />
              </div>
              <div>
                <label htmlFor="region" className="text-sm text-muted-foreground block mb-1">
                  Område
                </label>
                <Input
                  id="region"
                  value={region}
                  onChange={(e) => setRegion(e.target.value)}
                  placeholder="t.ex. Gotland, Roslagen, Österlen"
                />
              </div>
              <div>
                <label htmlFor="email" className="text-sm text-muted-foreground block mb-1">
                  E-post
                </label>
                <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required />
              </div>
              <div>
                <label htmlFor="message" className="text-sm text-muted-foreground block mb-1">
                  Vad är viktigt att besökare förstår innan de kommer?
                </label>
                <Textarea id="message" value={message} onChange={(e) => setMessage(e.target.value)} rows={6} />
                <p className="text-xs text-muted-foreground/60 mt-1">Du kan ändra detta senare.</p>
              </div>
            </div>

            {errorMsg && (
              <p className="text-sm text-destructive">{errorMsg}</p>
            )}

            <Button type="submit" disabled={submitting} className="w-full">
              {submitting ? 'Skickar...' : 'Skicka'}
            </Button>
          </form>
        </div>
      </main>
    </div>
  );
}
